import { useState, useEffect, useCallback, useRef } from 'react';
import CreativeEditorSDK from '@cesdk/cesdk-js';

/**
 * Simplified CESDK hook for dashboard components
 * This uses a minimal configuration to avoid conflicts with the editor
 */
export function useDashboardCESDK() {
  const cesdkRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const initializationAttemptedRef = useRef(false);

  // Initialize the editor
  const initialize = useCallback(async (container, options = {}) => {
    if (!container) {
      throw new Error('Container element is required for initialization');
    }
    
    // Check if we're on the editor page - if so, don't initialize
    const isEditorPage = window.location.pathname.includes('/editor');
    if (isEditorPage) {
      console.log("Skipping Dashboard CESDK initialization on editor page");
      setStatus('skipped');
      return null;
    }
    
    // Check if we're on checkout pages - if so, don't initialize
    const isCheckoutPage = window.location.pathname.includes('/checkout') || 
                          window.location.pathname.includes('/stripe-checkout') ||
                          window.location.pathname.includes('/order-success');
    if (isCheckoutPage) {
      console.log("Skipping Dashboard CESDK initialization on checkout page");
      setStatus('skipped');
      return null;
    }
    
    // Prevent multiple initialization attempts
    if (initializationAttemptedRef.current) {
      console.warn("Dashboard CESDK initialization already attempted");
      
      if (cesdkRef.current) {
        return cesdkRef.current;
      }
      
      // Return null instead of throwing an error to avoid console errors
      console.warn('Dashboard CESDK initialization in progress or failed');
      return null;
    }
    
    initializationAttemptedRef.current = true;
    
    try {
      setStatus('initializing');
      
      // Configure CESDK with minimal settings for dashboard use
      const config = {
        license: import.meta.env.VITE_CESDK_LICENSE,
        baseURL: "/cesdk-assets",
        theme: "light", // Use light theme for dashboard
        role: "Creator",
        ui: { 
          elements: { 
            view: 'default',
            panels: { settings: false },
            dock: { enabled: false },
            libraries: { enabled: false },
            navigation: { enabled: false },
            pages: { enabled: false }
          }
        },
        // Add CORS-related configuration
        fetchOptions: {
          mode: 'cors',
          credentials: 'same-origin'
        },
        ...options
      };

      if (options.userId) {
        config.userId = options.userId;
      }

      console.log("Initializing Dashboard CESDK with config:", config);
      
      try {
        const instance = await CreativeEditorSDK.create(container, config);
        console.log("Dashboard CESDK initialized successfully");

        // Store in ref for cleanup
        cesdkRef.current = instance;
        
        setStatus('ready');
        setIsReady(true);
        return instance;
      } catch (fetchError) {
        // If there's a CORS error, try to proceed without the editor
        console.warn("CESDK initialization CORS error - continuing without editor:", fetchError);
        setStatus('limited');
        setIsReady(true);
        return null;
      }
    } catch (err) {
      console.error('Dashboard CESDK initialization error:', err);
      setError(err);
      setStatus('error');
      initializationAttemptedRef.current = false; // Reset on error
      
      // Don't throw the error, just return null
      console.warn("Continuing without editor functionality");
      return null;
    }
  }, []);

  // Clean up the editor
  const cleanup = useCallback(() => {
    if (cesdkRef.current) {
      try {
        cesdkRef.current.dispose();
        cesdkRef.current = null;
        setStatus('idle');
        setIsReady(false);
        initializationAttemptedRef.current = false;
      } catch (err) {
        console.error('Error during Dashboard CESDK cleanup:', err);
      }
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    cesdk: cesdkRef.current,
    status,
    error,
    isReady,
    initialize,
    cleanup
  };
} 